import { Injectable, inject } from '@angular/core';
import { DvCheckboxElement, DvDynamicFormElement } from '@devinco/ui/legacy';
import { Entity } from '@shared/constants/entity';
import { CustomFleetMapSettings, FleetDto } from '@pages/fleet/interfaces';
import { MapMarker, MapInput } from '@features/map/interfaces';
import MarkerPopupUtils from '@features/map/utils/marker-popup.utils';
import DateUtils from '@shared/utils/date.utils';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import { FleetLocationDto } from '@pages/settings-page/fleet-settings/interfaces';
import TimedMapMarkerUtils from '@shared/components/timed-marker-map-menu-panel/timed-marker.utils';

@Injectable()
export class FleetMapService {
  private translate = inject(TranslateService);
  private api = inject(RestApiService);

  customFleetSettingElements: DvDynamicFormElement[] = [
    new DvCheckboxElement({
      colspan: 12,
      key: 'RegistrationNumber',
      checkboxLabel: 'FleetMapSettings.RegistrationNumber',
      rowspan: 2,
      extra: { defaultValue: true },
    }),
    new DvCheckboxElement({
      colspan: 12,
      key: 'UserDescription',
      checkboxLabel: 'FleetMapSettings.UserDescription',
      rowspan: 2,
      extra: { defaultValue: true },
    }),
    new DvCheckboxElement({
      colspan: 12,
      key: 'Description',
      checkboxLabel: 'FleetMapSettings.Description',
      rowspan: 2,
    }),
    new DvCheckboxElement({
      colspan: 12,
      key: 'Comment',
      checkboxLabel: 'FleetMapSettings.Comment',
      rowspan: 2,
    }),
  ];

  fetchGeofences(): Observable<FleetLocationDto[]> {
    const payload: GetRowsRequest = {
      entity: Entity.FleetLocation,
      fields: [
        'FleetLocationID',
        'GeoJson',
        'IsTemporary',
        'Description',
        'TemporaryFleetLocationTrackerObjectID',
      ],
    };
    return this.api
      .getData<FleetLocationDto>(payload)
      .pipe(map(resp => resp.data));
  }

  createMapMarkers(
    fleetItems: FleetDto[],
    customSettingsFormValue: CustomFleetMapSettings
  ): MapInput[] {
    const points: MapMarker[] = fleetItems
      .filter(item => item.LastGPSLat && item.LastGPSLon)
      .map(item => {
        const timeDifference = DateUtils.getTimeDifferenceInMinutes(
          item.LastActivityTime!,
          DateUtils.getCurrentNOTime()
        );
        const itemIconUrl = TimedMapMarkerUtils.getEntityIconByTimeDifference(
          timeDifference,
          item.VehicleID ? Entity.Vehicle : Entity.Asset
        );
        return {
          lat: item.LastGPSLat!,
          lng: item.LastGPSLon!,
          options: {
            riseOnHover: true,
            tooltipValue: this.getTooltipFromMapSettings(
              item,
              customSettingsFormValue
            ),
            isToolTipPermanent: true,
            rotationAngle: item.LastCourse,
            customMarker: {
              iconUrl: itemIconUrl,
              iconSize: [40, 40],
            },
            popup: {
              customElement: item.VehicleID
                ? MarkerPopupUtils.getVehiclePopupElementFromRegistry(
                    {
                      VehicleDescription: item.Description,
                      UserDescription: item.UserDescription,
                      Username: item.Username,
                      UserMobile: item.Mobile,
                      LastActivityTime: item.LastActivityTime,
                      LastGPSLat: item.LastGPSLat,
                      LastGPSLon: item.LastGPSLon,
                      TrackerObjectID: item.VehicleID,
                    },
                    itemIconUrl,
                    false,
                    true,
                    !!item.TemporaryGeolocation,
                    item.FleetID,
                    String(item.TemporaryFleetLocationID || '')
                  )
                : MarkerPopupUtils.getAssetPopupElementFromRegistry(
                    {
                      Description: item.Description,
                      Username: item.Username,
                      Department: item.DepartmentDescription,
                      LastActivityTime: item.LastActivityTime,
                      CurrentLocation: item.CurrentLocation,
                      Serial: item.Serial,
                      DocumentThumbnail: item.DocumentThumbnail,
                      Temperature: item.Temperature,
                      LastMoved: item.LastMoved,
                      LastGPSLat: item.LastGPSLat,
                      LastGPSLon: item.LastGPSLon,
                      AssetID: item.AssetID,
                    },
                    true,
                    item.FleetID
                  ),
              popupTableData: {
                iconHeader: this.translate.instant('General.Icon'),
                contentData: this.constructClusterPopupTableData(
                  item,
                  itemIconUrl
                ),
              },
            },
          },
        };
      });

    return [{ points }];
  }

  private constructClusterPopupTableData(
    fleetItem: FleetDto,
    iconUrl: string
  ): { [header: string]: string } {
    const popupData: { [header: string]: string } = {};
    popupData[this.translate.instant('General.Icon')] = iconUrl;
    popupData[this.translate.instant('LocationPopup.Description')] =
      fleetItem.Description || '';
    popupData[this.translate.instant('LocationPopup.Department')] =
      fleetItem.DepartmentDescription || '';
    popupData[this.translate.instant('LocationPopup.Property')] =
      fleetItem.VehicleTag || '';
    popupData[this.translate.instant('LocationPopup.LastActivity')] =
      fleetItem.LastActivityTime
        ? DateUtils.convertDateTimeToNO(new Date(fleetItem.LastActivityTime))
        : '';
    return popupData;
  }

  private getTooltipFromMapSettings(
    fleet: FleetDto,
    mapSettings?: CustomFleetMapSettings
  ): string | undefined {
    let tooltip = '';

    if (mapSettings?.RegistrationNumber) {
      tooltip += fleet.RegistrationNumber || '';
    }

    if (mapSettings?.UserDescription && fleet.UserDescription) {
      tooltip +=
        tooltip !== '' ? '<br>' + fleet.UserDescription : fleet.UserDescription;
    }

    if (mapSettings?.Description && fleet.Description) {
      tooltip +=
        tooltip !== '' ? '<br>' + fleet.Description : fleet.Description;
    }
    if (mapSettings?.Comment && fleet.Comment) {
      tooltip += tooltip !== '' ? '<br>' + fleet.Comment : fleet.Comment;
    }
    return tooltip !== '' ? tooltip : undefined;
  }
}
